/**
 * 🎨 [Phase v13.54 — 2026-07-21] Helper de formatage de durée live SANS AMBIGUÏTÉ.
 *
 * Problème résolu :
 *   Avant v13.54, plusieurs composants (MobileBottomNav, ChantierDuJour,
 *   PointageTerrain) affichaient les timers en format `${h}h${mm}` (ex: "8h55").
 *   Ce format était visuellement confondu avec les heures décimales
 *   "8,55h" (base 100 = 8h 33min réels) affichées côte à côte dans la
 *   même vue (cumul journée pour la paie).
 *
 * Contrat :
 *   - `formatDureeLive(diffSeconds)` retourne un string non-ambigu
 *     "Xh YYmin" (ex: "8h 55min") si diff >= 1h.
 *   - Sinon "YY min" (ex: "55 min") pour rester compact.
 *   - Toujours base 60 (horloge réelle), JAMAIS base 100 décimale.
 *   - À utiliser UNIQUEMENT pour les timers live (punch actif, pause en cours).
 *     Ne PAS utiliser pour les cumuls journée / paie / rapports (qui restent
 *     en base 100 décimale "8,55h" — contrat CCQ existant).
 *
 * Voir /app/memory/rca_v13_54_3_taches.md §Tâche 2.
 */

/**
 * Formate une durée en secondes vers un string non-ambigu.
 * @param {number} diffSeconds — Durée en secondes (>= 0)
 * @returns {string} — "Xh YYmin" ou "YY min" (ou "" si input invalide)
 */
export function formatDureeLive(diffSeconds) {
  const s = Math.max(0, Math.floor(Number(diffSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) {
    return `${h}h ${String(m).padStart(2, '0')}min`;
  }
  return `${m} min`;
}

/**
 * Variante avec secondes (pour timers qui tick à la seconde près).
 * Utile pour AutoPunchOut ou monitor terrain.
 * @param {number} diffSeconds
 * @returns {string} "Xh YYmin ZZs" ou "YYmin ZZs" ou "ZZ s"
 */
export function formatDureeLiveAvecSecondes(diffSeconds) {
  const total = Math.max(0, Math.floor(Number(diffSeconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}h ${mm}min ${ss}s`;
  if (m > 0) return `${m}min ${ss}s`;
  return `${s} s`;
}
